import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = {
  emp_code: "", name: "", father_name: "", uan: "", esi_no: "", designation: "", department: "", doj: "",
  phone: "", email: "", pan: "", bank_name: "", bank_account: "", ifsc: "", basic: 0, hra: 0, conv: 0,
  pf_applicable: true, esi_applicable: false, status: "active", notes: "",
};
const TEXT = [
  ["name", "Full name *"], ["father_name", "Father's name"], ["emp_code", "Employee code (auto if blank)"], ["designation", "Designation"],
  ["department", "Department"], ["doj", "Date of joining (as printed)"], ["uan", "UAN"], ["esi_no", "ESI / IP No."],
  ["pan", "PAN"], ["phone", "Phone"], ["email", "Email"], ["bank_name", "Bank name"], ["bank_account", "Bank account no."], ["ifsc", "IFSC"],
];

export default function EmployeeDialog({ employee, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...employee });
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(employee?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isEdit) await api.put(`/payroll/employees/${employee.id}`, form);
      else await api.post("/payroll/employees", form);
      toast.success(isEdit ? "Employee updated" : "Employee added");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-[720px]" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="text-sm">{isEdit ? `Edit · ${employee.name}` : "Add Employee"}</DialogTitle></DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {TEXT.map(([k, label]) => (
              <label key={k} className="block">
                <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">{label}</div>
                <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="h-8 text-[12px]" data-testid={`employee-field-${k}`} />
              </label>
            ))}
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">Monthly salary structure</div>
            <div className="grid grid-cols-3 gap-2.5">
              {[["basic", "Basic"], ["hra", "HRA"], ["conv", "Conveyance"]].map(([k, label]) => (
                <label key={k} className="block">
                  <div className="text-[10px] text-slate-500 mb-0.5">{label}</div>
                  <Input type="number" value={form[k] ?? 0} onChange={(e) => set(k, Number(e.target.value))} className="h-8 text-[12px] font-mono-num" data-testid={`employee-field-${k}`} />
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-5 text-[11.5px] text-slate-700">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!form.pf_applicable} onChange={(e) => set("pf_applicable", e.target.checked)} data-testid="employee-field-pf" /> PF member</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!form.esi_applicable} onChange={(e) => set("esi_applicable", e.target.checked)} data-testid="employee-field-esi" /> ESI member</label>
            <label className="flex items-center gap-1.5 ml-auto">
              Status
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="h-8 border border-slate-200 rounded-sm px-2 text-[12px] bg-white" data-testid="employee-field-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="h-8 text-[12px]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="h-8 text-[12px] bg-[#0F172A] text-white hover:bg-slate-800" data-testid="employee-save-btn">{saving ? "Saving…" : isEdit ? "Save changes" : "Add employee"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
